import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getBilet, getBiletlar, type Lang, type Question } from '@/api';
import { useAsync } from '@/hooks';
import { useLang } from '@/lang';
import { BiletResult } from '@/pages/practice/BiletResult';
import { Session, SessionResult } from '@/pages/practice/Session';
import { useLangSyncedQuestions } from '@/pages/practice/useLangSyncedQuestions';
import { biletLabel, type Strings } from '@/str';

export function BiletSession({
  initial,
  biletN,
  initialLang,
  t,
  onProgress,
  onFinish,
}: {
  initial: Question[];
  biletN: number;
  initialLang: Lang;
  t: Strings;
  onProgress?: (index: number) => void;
  onFinish?: () => void;
}) {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [round, setRound] = useState(0);
  const [result, setResult] = useState<SessionResult | null>(null);
  const biletlar = useAsync(getBiletlar);
  const next =
    !biletlar.loading && biletlar.error === null
      ? (biletlar.data.data.find((b) => b.number > biletN)?.number ?? null)
      : null;
  const fetchIn = useCallback((l: Lang) => getBilet(biletN, l).then((b) => b.questions), [biletN]);
  const questions = useLangSyncedQuestions(initial, initialLang, fetchIn);

  return (
    <>
      <Session
        key={round}
        questions={questions}
        t={t}
        onDone={() => {}}
        onProgress={onProgress}
        onFinish={onFinish}
        onResult={setResult}
        label={biletLabel(biletN, lang)}
      />
      {result && (
        <BiletResult
          result={result}
          total={questions.length}
          title={biletLabel(biletN, lang)}
          t={t}
          onRetake={() => {
            setResult(null);
            setRound((r) => r + 1);
          }}
          onNext={next === null ? undefined : () => navigate(`/practice/bilet/${next}`)}
          onClose={() => navigate('/biletlar', { state: { focus: biletN } })}
        />
      )}
    </>
  );
}
